import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Upload, message, Icon, Progress, Select, Button, Card, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { getAllTeams } from '../../utils/services/teamsAPI';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { importXMLBlocks } from '../../utils/xmlBlockImportService';
import { exportBlocksToXML } from '../../utils/xmlBlockImporter';
import { BulkUploadButton } from '../TemplateSection/styles';
import ManageBlocksModal from './ManageBlocksModal';
import './styles.css';

const { Option } = Select;

const ITEM_HEIGHT = 68;
const OVERSCAN = 5;

const NAMESPACES = ['XML_BLOCK_IMPORT'];

const XMLBlockImportPage = () => {
  const { t } = useTranslation(NAMESPACES);
  const { userDetails } = React.useContext(AppStoreContext);
  
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [importProgress, setImportProgress] = useState(null);
  const [blockStatuses, setBlockStatuses] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [cancelImport, setCancelImport] = useState(false);
  const [manageModalVisible, setManageModalVisible] = useState(false);

  // Fetch teams and categories on mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeams();
        if (response?.data) {
          setTeams(response.data);
          // Auto-select if only one team
          if (response.data.length === 1) {
            setSelectedTeamId(response.data[0].rgId);
          }
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        message.error('Failed to load teams');
      }
    };

    const fetchCategories = async () => {
      try {
        setFetchingCategories(true);
        const response = await getAllCategories(false, 'element');
        if (response?.statusCode === 507 && response?.data) {
          setCategories(response.data);
          // Auto-select if only one category
          if (response.data.length === 1) {
            setSelectedCategoryId(response.data[0].id);
          }
        } else {
          message.error('Failed to load categories');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        message.error('Failed to load categories');
      } finally {
        setFetchingCategories(false);
      }
    };
    
    fetchTeams();
    fetchCategories();
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (file) => {
    if (!selectedTeamId) {
      message.error('Please select a team first');
      return false;
    }

    if (!selectedCategoryId) {
      message.error('Please select a category first');
      return false;
    }

    if (!file.name.endsWith('.xml')) {
      message.error('Please upload an XML file');
      return false;
    }

    setLoading(true);
    setIsImporting(true);
    setCancelImport(false);
    setImportProgress(null);
    setBlockStatuses([]);
    setImportResult(null);

    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      // Progress callback
      const onProgress = (progressData) => {
        if (cancelImport) {
          return;
        }

        setImportProgress({
          stage: progressData.stage,
          message: progressData.message,
          currentBlock: progressData.currentBlock,
          currentLanguage: progressData.currentLanguage,
          blockIndex: progressData.blockIndex,
          totalBlocks: progressData.totalBlocks,
          processedBlocks: progressData.processedBlocks || 0,
          successfulImports: progressData.successfulImports || 0,
          failedImports: progressData.failedImports || 0,
          publishedBlocks: progressData.publishedBlocks,
          publishErrors: progressData.publishErrors,
        });

        // Update block statuses
        if (progressData.statusObj) {
          setBlockStatuses((prev) => {
            const existing = prev.findIndex(
              (s) => s.blockId === progressData.statusObj.blockId && s.language === progressData.statusObj.language
            );
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = { ...updated[existing], ...progressData.statusObj };
              return updated;
            }
            return [...prev, progressData.statusObj];
          });
        }
      };

      // Import blocks
      const result = await importXMLBlocks(fileContent, selectedTeamId, onProgress, selectedCategoryId, false);
      
      setImportResult(result);
      setBlockStatuses(result.blockStatuses || []);
      setIsImporting(false);
      setLoading(false);

      if (result.failedImports === 0) {
        message.success(`Successfully imported ${result.successfulImports} blocks`);
      } else {
        message.warning(
          `Import completed: ${result.successfulImports} successful, ${result.failedImports} failed`
        );
      }
    } catch (error) {
      console.error('Import error:', error);
      message.error(error.message || 'Failed to import XML file');
      setIsImporting(false);
      setLoading(false);
    }

    return false; // Prevent default upload behavior
  }, [selectedTeamId, selectedCategoryId, cancelImport]);

  const handleCancel = () => {
    setCancelImport(true);
    setIsImporting(false);
    setLoading(false);
    message.info('Import cancelled');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing':
        return 'processing';
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  /** Explicit styles for Block Status tags so FR: error / NL: success etc. are always readable */
  const getBlockStatusTagStyle = (status) => {
    switch (status) {
      case 'success':
        return { backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' };
      case 'error':
        return { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' };
      case 'processing':
        return { backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#1890ff' };
      default:
        return { backgroundColor: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.85)' };
    }
  };

  const getPublishStatusTagStyle = (publishStatus) => {
    switch (publishStatus) {
      case 'published':
      case 'qa-complete':
        return { backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' };
      case 'publish-error':
        return { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' };
      case 'qa-ready':
      case 'processing':
        return { backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#1890ff' };
      default:
        return { backgroundColor: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.85)' };
    }
  };

  const getPublishStatusIcon = (publishStatus) => {
    switch (publishStatus) {
      case 'processing':
        return <Icon type="loading" />;
      case 'qa-ready':
        return <Icon type="clock-circle" style={{ color: '#1890ff' }} />;
      case 'qa-complete':
        return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
      case 'published':
        return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
      case 'publish-error':
        return <Icon type="close-circle" style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  const getPublishStatusColor = (publishStatus) => {
    switch (publishStatus) {
      case 'processing':
        return 'processing';
      case 'qa-ready':
        return 'default';
      case 'qa-complete':
        return 'success';
      case 'published':
        return 'success';
      case 'publish-error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPublishStatusLabel = (publishStatus) => {
    switch (publishStatus) {
      case 'not-started':
        return 'Not Published';
      case 'processing':
        return 'Publishing...';
      case 'qa-ready':
        return 'QA Ready';
      case 'qa-complete':
        return 'QA Complete';
      case 'published':
        return 'Published';
      case 'publish-error':
        return 'Publish Error';
      default:
        return 'Unknown';
    }
  };

  const progressPercent = importProgress?.totalBlocks
    ? Math.round((importProgress.processedBlocks / importProgress.totalBlocks) * 100)
    : 0;

  // Group block statuses by block (as a stable array for virtual list)
  const groupedStatusList = useMemo(() => {
    const map = {};
    blockStatuses.forEach((status) => {
      const key = `${status.blockIndex}_${status.blockName || status.blockId || 'unknown'}`;
      if (!map[key]) {
        map[key] = { key, blockName: status.blockName || status.blockId || 'unknown', statuses: [] };
      }
      map[key].statuses.push(status);
    });
    return Object.values(map);
  }, [blockStatuses]);

  // Virtual scrolling state
  const statusContainerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const containerHeight = 500;

  const handleStatusScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const totalListHeight = groupedStatusList.length * ITEM_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(containerHeight / ITEM_HEIGHT) + 2 * OVERSCAN;
  const endIdx = Math.min(groupedStatusList.length, startIdx + visibleCount);
  const visibleItems = groupedStatusList.slice(startIdx, endIdx);
  const offsetTop = startIdx * ITEM_HEIGHT;

  return (
    <div className="xml-block-import-page">
      <Card
        title="XML Block Import"
        style={{ marginBottom: '20px' }}
        extra={
          <Button
            type="default"
            icon="delete"
            onClick={() => setManageModalVisible(true)}
            disabled={isImporting}
          >
            Manage Blocks
          </Button>
        }
      >
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Team:
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a team"
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            disabled={isImporting}
          >
            {teams.map((team) => (
              <Option key={team.rgId || team.id} value={team.rgId || team.id}>
                {team.groupName || team.name || `Team ${team.rgId || team.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Select Category: <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Select a category"
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
            loading={fetchingCategories}
            disabled={fetchingCategories || isImporting}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name}
              </Option>
            ))}
          </Select>
        </div>

        <Upload
          accept=".xml"
          showUploadList={false}
          beforeUpload={handleFileUpload}
          disabled={!selectedTeamId || !selectedCategoryId || isImporting}
        >
          <BulkUploadButton style={{ margin: 'auto', width: '50%' }}>
            <p className={loading ? 'downloadZip' : ''} style={{ visibility: loading ? 'visible' : 'hidden' }}>
              <Icon type="loading" style={{ marginRight: '5px', fontSize: '20px', color: '#FC6E51' }} />
            </p>
            <p className="import">
              {isImporting ? 'Importing...' : 'Upload XML File'}
            </p>
            <p className="desc">
              {isImporting
                ? 'Please wait while blocks are being imported'
                : 'Select an XML file to import blocks as standalone blocks'}
            </p>
          </BulkUploadButton>
        </Upload>

        {isImporting && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Button onClick={handleCancel} danger>
              Cancel Import
            </Button>
          </div>
        )}

        {importProgress && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Progress</h3>
            <Progress
              percent={progressPercent}
              status={importProgress.stage === 'error' ? 'exception' : 'active'}
              format={(percent) => `${percent}%`}
            />
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              {importProgress.message || (
                <>
                  {importProgress.processedBlocks || 0} of {importProgress.totalBlocks || 0} processed
                  {importProgress.currentBlock && (
                    <span style={{ marginLeft: 8, color: '#999' }}>
                      ({importProgress.currentBlock} / {importProgress.currentLanguage})
                    </span>
                  )}
                </>
              )}
            </div>
            {importProgress.totalBlocks && (
              <div style={{ marginTop: '10px' }}>
                <Tag color="green">Successful: {importProgress.successfulImports || 0}</Tag>
                <Tag color="red">Failed: {importProgress.failedImports || 0}</Tag>
                {importProgress.publishedBlocks !== undefined && (
                  <Tag color="blue">Published: {importProgress.publishedBlocks || 0}</Tag>
                )}
                {importProgress.publishErrors !== undefined && importProgress.publishErrors > 0 && (
                  <Tag color="orange">Publish Errors: {importProgress.publishErrors}</Tag>
                )}
                <Tag>Total: {importProgress.totalBlocks}</Tag>
              </div>
            )}
          </div>
        )}

        {groupedStatusList.length > 0 && (
          <div className="xml-block-import-page-block-status" style={{ marginTop: '30px' }}>
            <h3>Block Status ({groupedStatusList.length} blocks)</h3>
            <div
              ref={statusContainerRef}
              onScroll={handleStatusScroll}
              style={{
                height: containerHeight,
                overflowY: 'auto',
                border: '1px solid #e8e8e8',
                borderRadius: 4,
                background: '#fff',
              }}
            >
              <div style={{ height: totalListHeight, position: 'relative' }}>
                <div style={{ position: 'absolute', top: offsetTop, left: 0, right: 0 }}>
                  {visibleItems.map((group) => (
                    <div
                      key={group.key}
                      style={{
                        height: ITEM_HEIGHT,
                        boxSizing: 'border-box',
                        borderBottom: '1px solid #f0f0f0',
                        padding: '8px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{
                        fontWeight: 500,
                        fontSize: 13,
                        marginBottom: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {group.blockName}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden' }}>
                        {group.statuses.map((s) => (
                          <React.Fragment key={s.language}>
                            <Tag
                              color={getStatusColor(s.status)}
                              style={{ ...getBlockStatusTagStyle(s.status), margin: 0, fontSize: 11 }}
                            >
                              {s.language.toUpperCase()}: {s.status}
                            </Tag>
                            {s.publishStatus && s.publishStatus !== 'not-started' && (
                              <Tag
                                color={getPublishStatusColor(s.publishStatus)}
                                style={{ ...getPublishStatusTagStyle(s.publishStatus), margin: 0, fontSize: 11 }}
                              >
                                {getPublishStatusIcon(s.publishStatus)}
                                <span style={{ marginLeft: 3 }}>{getPublishStatusLabel(s.publishStatus)}</span>
                              </Tag>
                            )}
                            {s.errorMessage && (
                              <Tooltip title={s.errorMessage}>
                                <Tag style={{ backgroundColor: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', margin: 0, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {s.errorMessage}
                                </Tag>
                              </Tooltip>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {importResult && !isImporting && (
          <div style={{ marginTop: '30px' }}>
            <h3>Import Summary</h3>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <strong>Total Blocks:</strong> {importResult.totalBlocks}
                </div>
                <div>
                  <strong>Processed:</strong> {importResult.processedBlocks}
                </div>
                <div>
                  <strong>Successful:</strong> <Tag color="green">{importResult.successfulImports}</Tag>
                </div>
                <div>
                  <strong>Failed:</strong> <Tag color="red">{importResult.failedImports}</Tag>
                </div>
                {importResult.publishedBlocks !== undefined && (
                  <div>
                    <strong>Published:</strong> <Tag color="blue">{importResult.publishedBlocks}</Tag>
                  </div>
                )}
                {importResult.qaReadyBlocks !== undefined && importResult.qaReadyBlocks > 0 && (
                  <div>
                    <strong>QA Ready:</strong> <Tag color="default">{importResult.qaReadyBlocks}</Tag>
                  </div>
                )}
                {importResult.qaCompleteBlocks !== undefined && importResult.qaCompleteBlocks > 0 && (
                  <div>
                    <strong>QA Complete:</strong> <Tag color="success">{importResult.qaCompleteBlocks}</Tag>
                  </div>
                )}
                {importResult.publishErrors !== undefined && importResult.publishErrors > 0 && (
                  <div>
                    <strong>Publishing Errors:</strong> <Tag color="orange">{importResult.publishErrors}</Tag>
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <strong>Errors:</strong>
                    <ul style={{ marginTop: '5px' }}>
                      {importResult.errors.map((error, index) => (
                        <li key={index}>
                          {error.block} ({error.language}): {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.failedBlocks && importResult.failedBlocks.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        const xml = exportBlocksToXML(importResult.failedBlocks);
                        const blob = new Blob([xml], { type: 'application/xml' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'failed-blocks-export.xml';
                        a.click();
                        URL.revokeObjectURL(url);
                        message.success('Failed blocks exported as XML');
                      }}
                    >
                      Export failed blocks (XML)
                    </Button>
                    <span style={{ marginLeft: '8px', fontSize: '13px', color: '#666' }}>
                      {importResult.failedBlocks.length} block(s) – re-import this file after fixing issues
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </Card>

      <ManageBlocksModal
        visible={manageModalVisible}
        onClose={() => setManageModalVisible(false)}
        categories={categories}
      />
    </div>
  );
};

export default XMLBlockImportPage;

